import getInsightsTraceId from '../selectors/getInsightsTraceId';
import {
    EmailInsightType,
    FileInsightType,
    EventInsightType,
    InsightsDataFetchSource,
    convertInsightsEntrySourceEnumToName,
} from 'owa-meeting-insights-types-and-flights';
import { PerformanceDatapoint, logUsage } from 'owa-analytics';
import { userInteractionAction, ViewType } from 'owa-calendar-actions';
import type { ReadingPaneEntrySourceType } from 'owa-calendar-helpers-types/lib/ReadingPaneEntrySourceType';
import {
    getIsOrganizerFromMeetingId,
    getRelatedFilePosition,
    getRelatedEmailPosition,
    getSuggestionClickTimeRelevance,
    getRelatedEventPosition,
} from '../selectors/instrumentationSelectors';
import { getReminderInsightsProps } from '../selectors/insightsForReminderSelectors';
import {
    logInsightsSearchEntityActions,
    logInsightsResponseRendered,
    logInsightsClientLayout,
} from './insightsEventsHelpers';

export function logInsightsRenderAndLayout(
    meetingId: string,
    entrySource: InsightsDataFetchSource
) {
    // Log 3S instrumentation for ResponseRendered and ClientLayout
    logInsightsResponseRendered(meetingId, entrySource);
    logInsightsClientLayout(meetingId, entrySource);
}

export function logInsightsCacheHitMissRate(
    insightsPerfDatapoint: PerformanceDatapoint,
    meetingId: string,
    entrySource: InsightsDataFetchSource
) {
    if (!insightsPerfDatapoint) {
        // insightsPerfDatapoint can be null as it's called by both componentDidMount and componentDidUpdate, and could have been ended by either one
        return;
    }

    // We are repurposing traceId to represent if the related content insights are in cache.
    // Having a valid traceId means that there was a 3S call made before.
    const filesAndEmailsAreInCache = !!getInsightsTraceId(meetingId, entrySource);
    insightsPerfDatapoint.addCustomData({ cacheHit: filesAndEmailsAreInCache });
}

export function logFileInsightClicked(
    meetingId: string,
    file: FileInsightType,
    readingPaneEntrySource: ReadingPaneEntrySourceType | null,
    entrySource: InsightsDataFetchSource
) {
    const {
        fileExtension,
        isCloudyFile,
        isMeetingAttachment,
        relevanceRelationship,
        referenceId,
    } = file;
    const traceId = getInsightsTraceId(meetingId, entrySource);
    const position = getRelatedFilePosition(meetingId, referenceId, entrySource);
    const isOrganizer = getIsOrganizerFromMeetingId(meetingId);

    // log RelatedFileClicked on OWA side
    logUsage(
        'RelatedContentFileClicked',
        {
            position: position,
            fileType: fileExtension,
            isCloudy: isCloudyFile,
            isOrganizer: isOrganizer,
            isAttachment: isMeetingAttachment,
            relevanceRelationship: relevanceRelationship,
            readingPaneEntrySource: readingPaneEntrySource,
            entrySource: convertInsightsEntrySourceEnumToName(entrySource),
            timeRelevance: getSuggestionClickTimeRelevance(meetingId),
            traceId: traceId,
        },
        { isCore: true, excludeFromKusto: true }
    );

    // Log UserInteractions datapoint
    userInteractionAction(
        'LinkClicked',
        getUserInteractionViewType(entrySource),
        isOrganizer,
        true /* isMeeting */,
        ['FileInsight']
    );

    // log EntityClicked event to 3S
    logInsightsSearchEntityActions(meetingId, traceId, referenceId, entrySource);
}

export function logEmailInsightClicked(
    meetingId: string,
    email: EmailInsightType,
    readingPaneEntrySource: ReadingPaneEntrySourceType | null,
    entrySource: InsightsDataFetchSource
) {
    const { referenceId, relevanceRelationship } = email;
    const position = getRelatedEmailPosition(meetingId, referenceId, entrySource);
    const isOrganizer = getIsOrganizerFromMeetingId(meetingId);
    const traceId = getInsightsTraceId(meetingId, entrySource);

    logUsage(
        'RelatedContentEmailClicked',
        {
            position: position,
            isOrganizer: isOrganizer,
            relevanceRelationship: relevanceRelationship,
            readingPaneEntrySource: readingPaneEntrySource,
            entrySource: convertInsightsEntrySourceEnumToName(entrySource),
            timeRelevance: getSuggestionClickTimeRelevance(meetingId),
            traceId: traceId,
        },
        { isCore: true, excludeFromKusto: true }
    );

    // Log UserInteractions datapoint
    userInteractionAction(
        'LinkClicked',
        getUserInteractionViewType(entrySource),
        isOrganizer,
        true /* isMeeting */,
        ['EmailInsight']
    );

    // log EntityClicked event to 3S
    logInsightsSearchEntityActions(meetingId, traceId, referenceId, entrySource);
}

export function logEventInsightClicked(
    meetingId: string,
    event: EventInsightType,
    readingPaneEntrySource: ReadingPaneEntrySourceType | null,
    entrySource: InsightsDataFetchSource
) {
    const { referenceId, relevanceRelationship } = event;
    const position = getRelatedEventPosition(meetingId, referenceId, entrySource);
    const isOrganizer = getIsOrganizerFromMeetingId(meetingId);
    const traceId = getInsightsTraceId(meetingId, entrySource);

    logUsage(
        'RelatedContentEventClicked',
        {
            position: position,
            isOrganizer: isOrganizer,
            relevanceRelationship: relevanceRelationship,
            readingPaneEntrySource: readingPaneEntrySource,
            entrySource: convertInsightsEntrySourceEnumToName(entrySource),
            timeRelevance: getSuggestionClickTimeRelevance(meetingId),
            traceId: traceId,
        },
        { isCore: true, excludeFromKusto: true }
    );

    // Log UserInteractions datapoint
    userInteractionAction(
        'LinkClicked',
        getUserInteractionViewType(entrySource),
        isOrganizer,
        true /* isMeeting */,
        ['EventInsight']
    );

    // log EntityClicked event to 3S
    logInsightsSearchEntityActions(meetingId, traceId, referenceId, entrySource);
}

export function logInsightSummaryClicked(meetingId: string, entrySource: InsightsDataFetchSource) {
    const summaryType = getReminderInsightsProps(meetingId).insightType;

    logUsage(
        'RelatedContentSummaryClicked',
        {
            summaryType: summaryType,
            entrySource: convertInsightsEntrySourceEnumToName(entrySource),
        },
        { isCore: true, excludeFromKusto: true }
    );
}

function getUserInteractionViewType(entrySource: InsightsDataFetchSource): ViewType {
    switch (entrySource) {
        case InsightsDataFetchSource.TimePanelEventDetails:
            return 'TimePanelPeek';
        case InsightsDataFetchSource.AgendaView:
            return 'AgendaView';
        case InsightsDataFetchSource.ItemPeek:
            return 'SurfacePeek';
        default:
            return 'ReadForm';
    }
}
